import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { User } from "@/hooks/use-users";
import { useChangeRole } from "@/hooks/use-users";
import { ROLE_DISPLAY_NAMES, type USGRole } from "@/lib/rbac";

const USG_ROLES = Object.entries(ROLE_DISPLAY_NAMES) as [USGRole, string][];

interface ChangeRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

export function ChangeRoleDialog({ open, onOpenChange, user }: ChangeRoleDialogProps) {
  const [role, setRole] = useState(user.role);
  const changeRole = useChangeRole();

  function handleSubmit() {
    if (role === user.role) {
      onOpenChange(false);
      return;
    }
    changeRole.mutate({ userId: user.id, role }, { onSuccess: () => onOpenChange(false) });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Role</DialogTitle>
          <DialogDescription>Update the role for {user.name}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {USG_ROLES.map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={changeRole.isPending}>
            {changeRole.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
